export type ModuleKey = "hortifruti" | "graos" | "compras" | "pecuaria" | "loja";
export type RoleKey = "PRODUTOR" | "PRODUTOR_COMPRADOR" | "COMPRADOR" | "COOPERATIVA" | "DISTRIBUIDOR" | "TECNICO" | "LOJA_INSUMOS" | "FRETE" | "CARREGADOR";
export type ViewKey = ModuleKey | "produtos-loja" | "favoritos" | "meus-anuncios" | "perfil" | "contatos" | "produtores" | "compradores" | "fretes-carregadores" | "localizacao" | "menu" | "admin";

export type StoreBranch = {
  name: string;
  address: string;
  municipality?: string;
  state?: string;
  whatsapp?: string;
};

export type MediaImage = {
  id: string;
  url: string;
  storagePath?: string;
  alt?: string;
};

export type ProfessionalProfileData = {
  presentation?: string;
  name: string;
  whatsapp: string;
  municipality: string;
  municipalityCode: string;
  state: string;
  activityType: string;
  segments?: ModuleKey[];
  commercialPlaceName?: string;
  box?: string;
  representedStore?: string;
  representedStoreId?: string;
  branches?: StoreBranch[];
  businessHours?: string;
  address?: string;
  locationUrl?: string;
};

export type Profile = {
  id: string;
  name: string;
  role: RoleKey;
  roleLabel: string;
  initials: string;
  city: string;
  phone: string;
  verified: boolean;
  since: string;
  property: string;
  bio: string;
  seals: string[];
  avatar?: MediaImage;
  municipality?: string;
  municipalityCode?: string;
  state?: string;
  activityType?: string;
  segments?: ModuleKey[];
  commercialPlaceName?: string;
  box?: string;
  representedStore?: string;
  representedStoreId?: string;
  address?: string;
  branches?: StoreBranch[];
  businessHours?: string;
  locationUrl?: string;
};

export type ListingModerationStatus = "active" | "hidden" | "removed";

export type QuoteModule = "graos" | "pecuaria";

export type AgronomicGuide = {
  id: string;
  crop: string;
  inputType: "fertilizante" | "defensivo";
  objective: string;
  inputName: string;
  applicationStage: string;
  guidance: string;
  precautions: string;
  officialReference?: string;
  createdAt?: unknown;
  updatedAt?: unknown;
};

export type ProductLeaflet = {
  id: string;
  productName: string;
  activeIngredient: string;
  cultures: string[];
  targets: string;
  applicationSummary: string;
  safetyInterval: string;
  precautions: string;
  officialReference: string;
  createdAt?: unknown;
  updatedAt?: unknown;
};

export type MarketQuote = {
  id: string;
  module: QuoteModule;
  product: string;
  referenceRegion: string;
  averagePrice: number;
  unit: string;
  source: string;
  createdAt?: unknown;
  updatedAt?: unknown;
};

export type SupplyStore = {
  id: string;
  name: string;
  avatar?: MediaImage;
  municipality?: string;
  state?: string;
  whatsapp?: string;
  address?: string;
  businessHours?: string;
  locationUrl?: string;
  branches?: StoreBranch[];
  profileId?: string;
  ownerUid?: string;
  profileStatus?: "active" | "revoked";
  advertising?: { allowOptIn: boolean; allowPopup: boolean; optInLimit: number; popupLimit: number };
  sponsorSlot?: "vegetal" | "loja-02" | "loja-03";
  showInQuickFilter?: boolean;
  showOnLoading?: boolean;
  createdAt?: unknown;
  updatedAt?: unknown;
};

export type SponsoredStoreContent = {
  id: string;
  storeId: string;
  title: string;
  summary: string;
  body: string;
  author: string;
  category: string;
  published: boolean;
  createdAt?: unknown;
  updatedAt?: unknown;
};

export type PromotionCampaign = {
  id: string;
  storeId: string;
  title: string;
  message: string;
  category: string;
  state: string;
  municipality?: string;
  startsOn: string;
  endsOn: string;
  active: boolean;
  type?: "opt-in" | "popup";
  coverImage?: MediaImage;
  ownerProfileId?: string;
  createdAt?: unknown;
  updatedAt?: unknown;
};

export type PromotionPreference = {
  storeId: string;
  ownerUid: string;
  enabled: boolean;
  categories: string[];
  state: string;
  municipality?: string;
  updatedAt?: unknown;
};

export type CertificationSeal = {
  id: string;
  name: string;
  description: string;
  applicableRoles: RoleKey[];
  createdAt?: unknown;
  updatedAt?: unknown;
};

export type ProfileSealAssignment = {
  profileId: string;
  sealIds: string[];
  lastAuditEventId?: string;
  updatedAt?: unknown;
  updatedBy?: string;
};

export type SavedItemKind = "location" | "commercial-place" | "profile" | "supply-store";
export type SavedItem = {
  id: string;
  kind: SavedItemKind;
  targetId: string;
  name: string;
  subtitle?: string;
  role?: RoleKey;
  state?: string;
  municipality?: string;
  municipalityCode?: string;
  commercialPlaceId?: string;
  commercialPlaceName?: string;
  commercialPlaceType?: "ceasa" | "feira" | "outro";
  createdAt?: unknown;
};

export type Listing = {
  id: string;
  module: ModuleKey;
  ownerId: string;
  title: string;
  category: string;
  price: number | null;
  unit: string;
  quantity: string;
  location: string;
  municipality?: string;
  municipalityCode?: string;
  state?: string;
  stateName?: string;
  commercialPlaceId?: string;
  commercialPlaceName?: string;
  commercialPlaceType?: "ceasa" | "feira" | "outro";
  commercializationDate?: string;
  expirationDate?: string;
  distance: number;
  description: string;
  status: "publicado" | "negociando" | "vendido" | "atendido";
  image: string;
  images?: MediaImage[];
  emoji: string;
  featured?: boolean;
  organic?: boolean;
  onSale?: boolean;
  supplyStoreId?: string;
  responses: number;
  createdAt: string;
  moderationStatus?: ListingModerationStatus;
  moderatedAt?: unknown;
  moderatedBy?: string;
  moderationReason?: string;
  moderationEventId?: string;
  hiddenByAccountBlock?: boolean;
  accountBlockEventId?: string;
};

// A produção começa sem publicações pré-carregadas.
export const initialListings: Listing[] = [];

export const unavailableProfile: Profile = {
  id: "perfil-indisponivel",
  name: "Perfil indisponível",
  role: "COMPRADOR",
  roleLabel: "Contato indisponível",
  initials: "--",
  city: "Localização não informada",
  phone: "",
  verified: false,
  since: "",
  property: "Cadastro não localizado",
  bio: "Este perfil não está mais disponível para contato.",
  seals: [],
};

export const moduleMeta: Record<ModuleKey, { label: string; shortLabel: string; kicker: string; description: string; action: string; categories: string[]; accent: string }> = {
  hortifruti: { label: "Comercialização de Hortifruti", shortLabel: "Hortifruti", kicker: "Direto do produtor", description: "Frutas, verduras e legumes frescos próximos de você.", action: "Novo anúncio", categories: ["Todos", "Frutas", "Verduras", "Legumes", "Orgânicos"], accent: "#00bf63" },
  graos: { label: "Mercado de Grãos", shortLabel: "Grãos", kicker: "Ofertas de grãos", description: "Compare ofertas por saca, volume disponível e distância.", action: "Novo anúncio", categories: ["Todos", "Soja", "Milho", "Feijão", "Arroz", "Café"], accent: "#d3a72c" },
  compras: { label: "Lista de Compras", shortLabel: "Lista de Compras", kicker: "O que compradores procuram", description: "Encontre demandas abertas e envie sua proposta diretamente.", action: "Cadastrar demanda", categories: ["Todos", "Urgentes", "Hortifruti", "Grãos", "Pecuária"], accent: "#0c8d51" },
  pecuaria: { label: "Mercado Pecuário", shortLabel: "Pecuária", kicker: "Negócios do campo", description: "Bovinos, aves, suínos e produtos de origem animal.", action: "Novo anúncio", categories: ["Todos", "Bovinos", "Aves", "Suínos", "Leite", "Ovos"], accent: "#8b5f3d" },
  loja: { label: "Lojas de insumos", shortLabel: "Lojas de insumos", kicker: "Insumos e orientação", description: "Produtos para cultivo oferecidos por lojas e profissionais especializados.", action: "Cadastrar produto", categories: ["Todos", "Sementes", "Fertilizantes", "Defensivos", "Biológicos", "Ferramentas"], accent: "#16785a" },
};

export const roleCreateModules: Record<RoleKey, ModuleKey[]> = {
  PRODUTOR: ["hortifruti", "graos", "pecuaria"],
  PRODUTOR_COMPRADOR: ["hortifruti", "graos", "pecuaria", "compras"],
  COOPERATIVA: ["hortifruti", "graos", "pecuaria", "compras"],
  DISTRIBUIDOR: ["hortifruti", "graos", "pecuaria", "compras"],
  COMPRADOR: ["compras"],
  TECNICO: ["loja"],
  LOJA_INSUMOS: [],
  FRETE: [],
  CARREGADOR: [],
};

export const profileRequestOptions: Array<{ role: RoleKey; label: string; description: string }> = [
  { role: "PRODUTOR", label: "Produtor Rural", description: "Para quem anuncia produtos em Hortifruti, Grãos e Pecuária." },
  { role: "PRODUTOR_COMPRADOR", label: "Produtor/Comprador", description: "Para quem vende produtos e também publica demandas de compra." },
  { role: "COMPRADOR", label: "Comprador", description: "Para quem publica demandas na Lista de Compras." },
  { role: "COOPERATIVA", label: "Cooperativa", description: "Para cooperativas que vendem produtos e publicam demandas de compra." },
  { role: "DISTRIBUIDOR", label: "Distribuidor", description: "Para distribuidores que vendem produtos e publicam demandas de compra." },
  { role: "TECNICO", label: "Técnico Agropecuário", description: "Para profissionais que representam lojas e anunciam produtos nas Lojas de insumos." },
  { role: "LOJA_INSUMOS", label: "Loja de insumos", description: "Para cadastrar a loja, integrar publicações e administrar comunicações patrocinadas autorizadas." },
  { role: "CARREGADOR", label: "Carregador", description: "Para profissionais e equipes de carga e descarga disponíveis para contato." },
  { role: "FRETE", label: "Frete", description: "Para transportadores e empresas de frete disponíveis para contato." },
];

export type ProfileApprovalMode = "self-service" | "admin";
export const profileApprovalPolicy: Record<RoleKey, ProfileApprovalMode> = {
  PRODUTOR: "self-service",
  PRODUTOR_COMPRADOR: "self-service",
  COOPERATIVA: "self-service",
  DISTRIBUIDOR: "self-service",
  COMPRADOR: "self-service",
  TECNICO: "admin",
  LOJA_INSUMOS: "admin",
  FRETE: "self-service",
  CARREGADOR: "self-service",
};

export const requiresAdminApproval = (role: RoleKey) => profileApprovalPolicy[role] === "admin";

export const roleRespondModules: Record<RoleKey, ModuleKey[]> = {
  PRODUTOR: ["hortifruti", "graos", "pecuaria", "compras"],
  PRODUTOR_COMPRADOR: ["hortifruti", "graos", "pecuaria", "compras"],
  COOPERATIVA: ["hortifruti", "graos", "pecuaria", "compras"],
  DISTRIBUIDOR: ["hortifruti", "graos", "pecuaria", "compras"],
  COMPRADOR: ["compras"],
  TECNICO: ["loja"],
  LOJA_INSUMOS: [],
  FRETE: [],
  CARREGADOR: [],
};

export const canCreate = (role: RoleKey, module: ModuleKey) => roleCreateModules[role].includes(module);
export const canRespond = (role: RoleKey, module: ModuleKey) => roleRespondModules[role].includes(module);
const profileDirectoryIndexes = new WeakMap<Profile[], Map<string, Profile>>();

export const ownerOf = (ownerId: string, directory: Profile[]) => {
  let index = profileDirectoryIndexes.get(directory);
  if (!index) {
    index = new Map(directory.map((profile) => [profile.id, profile]));
    profileDirectoryIndexes.set(directory, index);
  }
  return index.get(ownerId) ?? unavailableProfile;
};

export function listingImages(listing: Listing): MediaImage[] {
  if (listing.images?.length) return listing.images.slice(0, 5);
  return listing.image ? [{ id: `legacy-${listing.id}`, url: listing.image, alt: listing.title }] : [];
}

export const listingCover = (listing: Listing) => listingImages(listing)[0]?.url ?? "";
export const listingModerationStatus = (listing: Listing): ListingModerationStatus => listing.moderationStatus ?? "active";

const brazilCurrencyFormatter = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" });

export function formatPrice(price: number | null) {
  return price === null ? "A combinar" : brazilCurrencyFormatter.format(price);
}
